import { memo } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DeleteOutline, DragIndicator } from '@mui/icons-material'
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { SmepEventOnCallPerson } from '@nativewaves/nw-cloud-sdk/smep'
import { useTranslation } from 'react-i18next'

import { onCallPersonLabel } from 'domains/Workspaces/Events/Event/onCall'

export type PersonRowProps = {
  /** Field array key — the sortable id, since a person can be added and removed within one edit. */
  id: string
  person: SmepEventOnCallPerson
  /** Zero-based position in the escalation order. */
  index: number
  onRemove: () => void
}

const PersonRowRaw = ({ id, person, index, onRemove }: PersonRowProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  return (
    <Stack
      ref={setNodeRef}
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ py: 1, position: 'relative', zIndex: isDragging ? 1 : 'auto', opacity: isDragging ? 0.6 : 1 }}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Tooltip title={t('domain:Workspace.Event.OnCall.reorder')}>
        <IconButton
          ref={setActivatorNodeRef}
          size="small"
          sx={{ cursor: 'grab', touchAction: 'none' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
      </Tooltip>

      <Chip size="small" label={index + 1} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="text" noWrap>
          {onCallPersonLabel(person)}
        </Typography>
        <Typography sx={{ color: 'text.secondary' }} variant="text-sm" noWrap>
          {[person.email, person.phoneNumber].filter(Boolean).join(' · ') ||
            t('domain:Workspace.Event.OnCall.noContactDetails')}
        </Typography>
      </Box>

      <Tooltip title={t('domain:Workspace.Event.OnCall.removePerson')}>
        <IconButton size="small" onClick={onRemove}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

export const PersonRow = memo(PersonRowRaw)
